import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { assign, filter, keys, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { assetCurrencyExchangeApi } from 'api';
import {
  CloseFormikDialogResult,
  Dialog,
  DialogProps,
  FormControls,
  FormikSelect,
  FormikTextField,
  FormikYesNoRadioGroup,
} from 'components';
import { CurrencyExchangeDirection, QueryKey } from 'enums';
import { useCurrencies, useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AssetCurrencyExchange, AssetCurrencyExchangeDto } from 'types';
import { validationUtils } from 'utils';

type Values = AssetCurrencyExchangeDto;

type Props = DialogProps<AssetCurrencyExchange>;

export const AssetCurrencyExchangeDetailsDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
  ...rest
}) => {
  const queryClient = useQueryClient();
  // TODO: move tKeys
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.currencies.asset_currency_exchange_details',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const isNew = useMemo(() => !data, [data]);

  const {
    assetCurrenciesOptions,
    getCurrencyExchangeOptions,
    getAssetCurrencyCode,
  } = useCurrencies();

  const { mutate: createCurrencyExchange } = useMutation<
    AssetCurrencyExchange,
    AxiosError,
    Partial<AssetCurrencyExchange>
  >(assetCurrencyExchangeApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.AssetCurrencyExchange);
      onClose({ ok: true });
    },
  });

  const { mutate: updateCurrencyExchange } = useMutation<
    AssetCurrencyExchange,
    AxiosError,
    { id: string; data: Partial<AssetCurrencyExchange> }
  >(assetCurrencyExchangeApi.update, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.AssetCurrencyExchange);
      onClose({ ok: true });
    },
  });

  const title = useMemo(
    () => (data ? t('title.edit') : t('title.create')),
    [t, data],
  );

  const getInitialValues = useCallback(
    (): Values => ({
      name: '',
      sellExchange: false,
      buyExchange: false,
      statisticExchange: false,
      assetCurrencyId: '',
      crossAssetCurrencyId: '',
      currencyExchangeId: '',
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        sellExchange: Yup.boolean(),
        buyExchange: Yup.boolean(),
        statisticExchange: Yup.boolean(),
        assetCurrencyId: Yup.string().required(tCommon('errors.required')),
        crossAssetCurrencyId: Yup.string().required(tCommon('errors.required')),
        currencyExchangeId: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result?.data?.formikHelpers.resetForm({ values: getInitialValues() });
        onClose(result);
        return;
      }
      if (result.data?.values) {
        const options = {
          onSuccess: () => {
            result?.data?.formikHelpers.resetForm({
              values: getInitialValues(),
            });
          },
          onError: (error: AxiosError) => {
            result.data?.formikHelpers.setErrors(
              validationUtils.getFormErrors(error),
            );
          },
        };
        if (!data) {
          createCurrencyExchange(result.data.values, options);
        } else {
          updateCurrencyExchange(
            {
              id: data.id!,
              data: result.data.values,
            },
            options,
          );
        }
      }
    },
    [
      data,
      getInitialValues,
      onClose,
      createCurrencyExchange,
      updateCurrencyExchange,
    ],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const formatExchangeLabel = useCallback(
    (label: string, values: Values, direction: CurrencyExchangeDirection) => {
      if (!values.assetCurrencyId || !values.crossAssetCurrencyId) {
        return label;
      }
      const codes = [
        getAssetCurrencyCode(values.assetCurrencyId),
        getAssetCurrencyCode(values.crossAssetCurrencyId),
      ];
      if (direction === CurrencyExchangeDirection.Sell) {
        codes.reverse();
      }
      return `${label} (${codes.join(' → ')})`;
    },
    [getAssetCurrencyCode],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(assign(_initialValues, pick(data, keys(_initialValues))));
  }, [data, getInitialValues]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Dialog
          open={open}
          title={title}
          data={{ values: formik.values, formikHelpers: formik }}
          okDisabled={!formik.isValid}
          onClose={handleClose}
          {...rest}
        >
          <Form>
            <FormControls>
              <FormikTextField label={t('fields.name')} name="name" required />
              <FormikSelect
                label={t('fields.asset_currency')}
                name="assetCurrencyId"
                required
                options={filter(
                  assetCurrenciesOptions,
                  (item) => item.value !== formik.values.crossAssetCurrencyId,
                )}
                noneOption
                disabled={!isNew}
              />
              <FormikSelect
                label={t('fields.cross_asset_currency')}
                name="crossAssetCurrencyId"
                required
                options={filter(
                  assetCurrenciesOptions,
                  (item) => item.value !== formik.values.assetCurrencyId,
                )}
                noneOption
                disabled={!isNew}
              />
              <FormikSelect
                label={t('fields.currency_exchange')}
                name="currencyExchangeId"
                required
                options={getCurrencyExchangeOptions({
                  assetCurrencyId: formik.values.assetCurrencyId,
                })}
              />
              <FormikYesNoRadioGroup
                label={formatExchangeLabel(
                  t('fields.sell_exchange'),
                  formik.values,
                  CurrencyExchangeDirection.Sell,
                )}
                name="sellExchange"
              />
              {/* Temporarly hidden until functionality will be needed.
                 - shops: use own config, default not allowed yet
                 - traders: not allowed yet
                 - providers: not allowed yet
                  */}
              {/* <FormikYesNoRadioGroup
                label={formatExchangeLabel(
                  t('fields.buy_exchange'),
                  formik.values,
                  CurrencyExchangeDirection.Buy,
                )}
                name="buyExchange"
              /> */}
              <FormikYesNoRadioGroup
                label={t('fields.statistic_exchange')}
                name="statisticExchange"
              />
            </FormControls>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};
